import {
  formatRigFracCountyLabel,
  formatRigFracStateLabel,
  rigFracCountyFieldName,
  rigFracSubBasinFieldName
} from "utils/map/rigFracValueMapping";

import { RigFracPointRow } from "api/rigfrac";

// Rig/Frac Data Table's own row + column types. Deliberately NOT
// `EntityKind`-typed — no `RigFrac` member is added to `models/entityKind.ts`.
export type {
  RigFracBbox,
  RigFracFilters,
  RigFracPointRow,
  RigFracPointsPage,
  RigFracSort,
  RigFracSpatial
} from "api/rigfrac";

/**
 * Displayed in place of a missing Rig Trajectory (EVA-107). Deliberately a
 * display constant, not `NA_MATCH_KEY` from focusColorSync — that one is a
 * lowercased *matching* key for colour-lock comparison. Same text today, but
 * they answer different questions and shouldn't be coupled.
 */
const NO_TRAJECTORY_LABEL = "n/a";

export type RigFracColumnKey =
  | "activityType"
  | "activityDate"
  | "activityCount"
  | "operator"
  | "serviceCompany"
  | "rigNumber"
  | "basin"
  | "subBasin"
  | "stateProvince"
  | "countyUnique"
  | "country"
  | "locationType"
  | "rigTrajectory"
  | "lat"
  | "lng"
  | "isCurrentlyActive";

export interface RigFracColumnDef {
  key: RigFracColumnKey;
  name: string;
  width: number;
  /** Formats a cell value for grid display, copy, and export alike (single source of truth). */
  format: (row: RigFracPointRow) => string;
}

/**
 * Full displayable column catalogue, in default order. `id` (the Mongo
 * `_id`) is intentionally not a displayable column — it's meaningless to
 * users — but remains on `RigFracPointRow` as the stable react-data-grid row
 * key (see `rowKeyGetter` in RigFracDataTableWidget.tsx).
 */
export const RIGFRAC_COLUMNS: RigFracColumnDef[] = [
  { key: "activityType", name: "Type", width: 90, format: (r) => r.activityType },
  {
    key: "activityDate",
    name: "Activity Date",
    width: 130,
    format: (r) => r.activityDate
  },
  { key: "operator", name: "Operator", width: 170, format: (r) => r.operator },
  // Service Company and Rig Number are hidden until Canadian crews are added —
  // the source data has no values for them yet. Both still arrive on
  // RigFracPointRow and keep their RigFracColumnKey members, so re-showing them
  // is a matter of restoring these two entries:
  //   { key: "serviceCompany", name: "Service Company", width: 170,
  //     format: (r) => r.serviceCompany },
  //   { key: "rigNumber", name: "Rig Number", width: 120,
  //     format: (r) => r.rigNumber },
  { key: "country", name: "Country", width: 130, format: (r) => r.country ?? "" },
  {
    key: "stateProvince",
    name: "State/Province",
    width: 140,
    format: (r) => formatRigFracStateLabel(r.stateProvince)
  },
  {
    key: "countyUnique",
    get name() {
      return rigFracCountyFieldName("County");
    },
    width: 150,
    format: (r) => formatRigFracCountyLabel(r.countyUnique, r.stateProvince)
  },
  { key: "basin", name: "Basin", width: 130, format: (r) => r.basin },
  {
    key: "subBasin",
    get name() {
      return rigFracSubBasinFieldName("Sub-Basin");
    },
    width: 130,
    format: (r) => r.subBasin
  },
  {
    key: "activityCount",
    name: "Count",
    width: 90,
    // activityCount 0 must render/export as "0", never blank/dropped.
    format: (r) => String(r.activityCount)
  },
  {
    key: "isCurrentlyActive",
    name: "Latest",
    width: 90,
    format: (r) => (r.isCurrentlyActive ? "True" : "False")
  },
  {
    key: "locationType",
    name: "Location Type",
    width: 140,
    format: (r) => r.locationType
  },
  {
    key: "rigTrajectory",
    name: "Rig Trajectory",
    width: 140,
    // Null for frac crews (no rig) and for rows Novi hasn't classified (~31%).
    // Rendered as "n/a" rather than blank so the absence is explicit — a blank
    // cell reads as a rendering bug at this frequency. Exports use this same
    // formatter, so CSV/Excel carry "n/a" too.
    format: (r) => r.rigTrajectory ?? NO_TRAJECTORY_LABEL
  },
  { key: "lng", name: "Longitude", width: 110, format: (r) => r.lng.toFixed(4) },
  { key: "lat", name: "Latitude", width: 110, format: (r) => r.lat.toFixed(4) }
];

export function getColumnDef(key: RigFracColumnKey): RigFracColumnDef {
  const def = RIGFRAC_COLUMNS.find((c) => c.key === key);
  if (!def) {
    throw new Error(`Unknown RigFrac column key: ${key}`);
  }
  return def;
}
